/**
 * GRAFİK YAKINLAŞTIRMA (kullanıcı, 15.10.2026) — Günlük Analiz ve Dönemsel
 * Getiri aynı kuralı kullanır.
 *
 * Görünen aralık bir gün penceresidir: `bas`..`son` (ikisi dahil, serinin
 * indeksleri). Yakınlaşma yalnızca ZAMAN ekseninde; dikey eksen görünen
 * aralığa göre yeniden ölçeklenir (çizen bileşen yapar). Hesap yok, yalnızca
 * gösterim: noktalar motorun sayıları.
 */

import { useCallback, useState } from "react";

export interface Pencere {
  readonly bas: number;
  readonly son: number;
}

/** Bundan dar pencereye yakınlaşılmaz (bir haftalık görünüm). */
export const EN_AZ_GUN = 7;

export function tamPencere(n: number): Pencere {
  return { bas: 0, son: Math.max(n - 1, 0) };
}

function sinirla(bas: number, genislik: number, n: number): Pencere {
  const g = Math.min(Math.max(genislik, Math.min(EN_AZ_GUN, n)), n);
  const b = Math.min(Math.max(bas, 0), n - g);
  return { bas: b, son: b + g - 1 };
}

/** Pencereyi `merkez` çevresinde `oran` katına getirir (0,5 yakınlaş, 2 uzaklaş). */
export function olcekle(p: Pencere, merkez: number, oran: number, n: number): Pencere {
  if (n <= 1) return tamPencere(n);
  const genislik = p.son - p.bas + 1;
  const yeni = Math.round(genislik * oran);
  const m = Math.min(Math.max(merkez, p.bas), p.son);
  // Merkezin pencere içindeki göreli yeri korunur: imleç altındaki gün kaymaz.
  const goreli = genislik <= 1 ? 0.5 : (m - p.bas) / (genislik - 1);
  return sinirla(Math.round(m - goreli * (yeni - 1)), yeni, n);
}

/** Sürüklenen aralık; çok darsa (tıklama) null. */
export function araliktan(a: number, b: number, n: number): Pencere | null {
  const bas = Math.min(a, b);
  const son = Math.max(a, b);
  if (son - bas + 1 < Math.min(EN_AZ_GUN, n)) return null;
  return sinirla(bas, son - bas + 1, n);
}

export interface Yakinlastirma {
  readonly pencere: Pencere;
  /** Pencere serinin tamamı mı. */
  readonly tam: boolean;
  readonly yakinlas: (merkez: number) => void;
  readonly uzaklas: (merkez: number) => void;
  readonly sifirla: () => void;
  readonly araligaGit: (a: number, b: number) => boolean;
}

/**
 * Seri uzunluğu değişince (yeni aralık, yeni veri) pencere AYNI ÇİZİMDE
 * tümüne döner: pencere kurulduğu uzunlukla birlikte tutulur. (Önceki hâli
 * sıfırlamayı bir efektle yapıyordu; veri gelince ilk çizim eski — boş
 * seriye ait — pencereyle tek noktalık çiziliyordu. Paralel oturum yakaladı,
 * `grafik.test.tsx` "grafik dolarak gelmez", 15.10.2026.)
 */
export function useYakinlastirma(n: number): Yakinlastirma {
  const [durum, setDurum] = useState<{ readonly n: number; readonly p: Pencere }>(() => ({ n, p: tamPencere(n) }));
  const p = durum.n === n ? durum.p : tamPencere(n);
  const guncelle = useCallback(
    (f: (x: Pencere) => Pencere | null) =>
      setDurum((d) => {
        const once = d.n === n ? d.p : tamPencere(n);
        const yeni = f(once);
        return yeni === null ? { n, p: once } : { n, p: yeni };
      }),
    [n],
  );
  const yakinlas = useCallback((m: number) => guncelle((x) => olcekle(x, m, 0.5, n)), [guncelle, n]);
  const uzaklas = useCallback((m: number) => guncelle((x) => olcekle(x, m, 2, n)), [guncelle, n]);
  const sifirla = useCallback(() => guncelle(() => tamPencere(n)), [guncelle, n]);
  const araligaGit = useCallback(
    (a: number, b: number) => {
      const yeni = araliktan(a, b, n);
      if (yeni !== null) guncelle(() => yeni);
      return yeni !== null;
    },
    [guncelle, n],
  );
  return { pencere: p, tam: p.bas === 0 && p.son >= n - 1, yakinlas, uzaklas, sifirla, araligaGit };
}
